/**
 * Storage Helper - Provides safe localStorage access with fallback for restricted browsers
 *
 * Facebook's in-app browser and some other mobile browsers block localStorage.
 * This utility provides a transparent fallback to in-memory storage.
 */

class StorageHelper {
    private static inMemoryStorage: Map<string, string> = new Map();
    private static isLocalStorageAvailable: boolean | null = null;

    /**
     * Check if localStorage is available and working
     */
    private static checkLocalStorage(): boolean {
        if (this.isLocalStorageAvailable !== null) {
            return this.isLocalStorageAvailable;
        }

        try {
            const testKey = '__storage_test__';
            localStorage.setItem(testKey, 'test');
            localStorage.removeItem(testKey);
            this.isLocalStorageAvailable = true;
            return true;
        } catch (e) {
            this.isLocalStorageAvailable = false;
            console.warn('localStorage NOT available - using in-memory fallback:', e);
            return false;
        }
    }

    /**
     * Detect if user is in Facebook's in-app browser
     */
    public static isFacebookBrowser(): boolean {
        const ua = navigator.userAgent || navigator.vendor;
        return (ua.indexOf('FBAN') > -1) || (ua.indexOf('FBAV') > -1) || (ua.indexOf('Instagram') > -1);
    }

    /**
     * Get item from storage (localStorage or fallback)
     */
    public static getItem(key: string): string | null {
        if (this.checkLocalStorage()) {
            try {
                return localStorage.getItem(key);
            } catch (e) {
                console.error('Error reading from localStorage:', e);
                return this.inMemoryStorage.get(key) || null;
            }
        }
        return this.inMemoryStorage.get(key) || null;
    }

    /**
     * Set item in storage (localStorage or fallback)
     */
    public static setItem(key: string, value: string): void {
        if (this.checkLocalStorage()) {
            try {
                localStorage.setItem(key, value);
                return;
            } catch (e) {
                console.error('Error writing to localStorage:', e);
            }
        }
        // Fallback to in-memory storage
        this.inMemoryStorage.set(key, value);
    }

    /**
     * Remove item from storage (localStorage or fallback)
     */
    public static removeItem(key: string): void {
        if (this.checkLocalStorage()) {
            try {
                localStorage.removeItem(key);
                return;
            } catch (e) {
                console.error('Error removing from localStorage:', e);
            }
        }
        this.inMemoryStorage.delete(key);
    }

    /**
     * Clear all storage (localStorage or fallback)
     */
    public static clear(): void {
        if (this.checkLocalStorage()) {
            try {
                localStorage.clear();
                return;
            } catch (e) {
                console.error('Error clearing localStorage:', e);
            }
        }
        this.inMemoryStorage.clear();
    }

    /**
     * Check if we're using fallback storage
     */
    public static isUsingFallback(): boolean {
        return !this.checkLocalStorage();
    }
}

export default StorageHelper;
